import { durationHumanize } from 'services/utils';
import { buildContactGroupInfo } from '../alert-templates/contact-group-utils';

export default class EscalationBuilderController {

    /* @ngInject */
    constructor($scope, FeatureFlags, IAMService, ToastService, UserPreferencesManager, ProjectManager) {
        this.$scope = $scope;
        this.FeatureFlags = FeatureFlags;
        this.IAMService = IAMService;
        this.ToastService = ToastService;
        this.UserPreferencesManager = UserPreferencesManager;
        this.ProjectManager = ProjectManager;
    }

    $onInit() {
        // Regex patterns
        this.webhookUrlRegex = /^https:\/\/.+/;
        this.emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
        this.phoneRegex = /^\+[1-9]\d{9,14}$/;
        this.phoneCallRegex = /^\+[1-9]\d{9,14}(,(,|[0-9]|#|\*){1,19})?$/;

        // State
        this.lastActionFocus = null;
        this.availableDynamicInputs = [];
        this.computedWarnings = {};
        this.expandedWarnings = new Set();
        this.contactGroupInfo = new Map();
        this.pendingContactGroupFetches = new Set();

        // Dynamic inputs
        this.updateDynamicInputs();
    }

    $onChanges(changes) {
        if (changes.triggerField) {
            this.updateDynamicInputs();
        }
    }

    // --- Escalation level management ---

    addEscalationLevel() {
        const level = {
            displayName: `Level ${this.escalationLevels.length + 1}`,
            actions: [],
            escalateAfter: '7200s',
        };
        this.escalationLevels.push(level);
        this.notifyLevelsChange();
    }

    removeEscalationLevel(index) {
        this.escalationLevels.splice(index, 1);
        this.notifyLevelsChange();
    }

    escalationDescription(escalation) {
        const index = this.escalationLevels.indexOf(escalation);
        if (index === 0) {
            if (this.triggerField) {
                return 'Will be sent when trigger condition is met';
            }
            return 'Will be sent when trigger condition is met';
        }

        const previousEscalation = this.escalationLevels[index - 1];
        const humanizedDelay = durationHumanize(previousEscalation.escalateAfter);
        return `Will be sent ${humanizedDelay} after the previous escalation if the alert remains unacknowledged, unaddressed, or unresolved.`;
    }

    hasActions(escalation) {
        return escalation && escalation.actions && escalation.actions.length > 0;
    }

    // --- Action management ---

    addAction(escalation, actionType) {
        let targetEscalation = escalation;
        if (!targetEscalation) {
            if (this.escalationLevels.length === 0) {
                this.addEscalationLevel();
                targetEscalation = this.escalationLevels[0];
            } else {
                targetEscalation = this.escalationLevels[0];
            }
        }

        const action = this.createActionByType(actionType);
        targetEscalation.actions.push(action);
        this.notifyLevelsChange();
    }

    removeAction(escalation, actionIndex) {
        escalation.actions.splice(actionIndex, 1);
        this.notifyLevelsChange();
    }

    createActionByType(type) { // eslint-disable-line class-methods-use-this
        const baseAction = { type };

        switch (type) {
            case 'EMAIL':
                return {
                    ...baseAction,
                    email: {
                        recipients: [],
                        contactGroups: [],
                        subject: 'Alert — $name in $projectDisplayName',
                        body: 'Device $name in $projectDisplayName has been in alert state for $timeSinceTrigger.\n\nTriggered at: $triggerTime\nDevice ID: $deviceID'
                    }
                };
            case 'SMS':
                return {
                    ...baseAction,
                    sms: {
                        recipients: [],
                        contactGroups: [],
                        body: 'Alert: $name in $projectDisplayName triggered for $timeSinceTrigger.'
                    }
                };
            case 'PHONE_CALL':
                return {
                    ...baseAction,
                    phoneCall: {
                        recipients: [],
                        contactGroups: [],
                        introduction: 'This is an automated call from Disruptive Technologies.',
                        message: 'Alert for $name in $projectDisplayName. The device has been triggered for $timeSinceTrigger.'
                    }
                };
            case 'WEBHOOK':
                return { ...baseAction, webhook: { url: '', headers: {} } };
            case 'CORRIGO':
                return {
                    ...baseAction,
                    corrigo: {
                        assetId: '', taskId: '', customerId: '', clientId: '',
                        clientSecret: '', companyName: '', subTypeId: '',
                        contactName: '', contactAddress: '',
                        workOrderDescription: this.defaultCorrigoWorkOrderDescription
                    }
                };
            case 'SERVICE_CHANNEL':
                return { ...baseAction, serviceChannel: { storeId: '', assetTagId: '', trade: '', description: '' } };
            case 'JOLT_LIST':
                return {
                    ...baseAction,
                    joltList: {
                        displayName: '', listTemplateId: '', locationId: '',
                        timeToComplete: '', userName: '', password: '', appId: '', companyId: ''
                    }
                };
            default:
                return baseAction;
        }
    }

    get defaultCorrigoWorkOrderDescription() {
        return 'Created by a DT Sensor via API. DT Studio Dashboard: https://studio.disruptive-technologies.com/projects/PROJECT_ID/dashboard';
    }

    // --- Feature flags ---

    hasCorrigoAction() {
        return this.FeatureFlags.isActive('notifications_corrigo');
    }

    hasServiceChannelAction() {
        return this.FeatureFlags.isActive('notifications_service_channel');
    }

    hasJoltListAction() {
        return this.FeatureFlags.isActive('notifications_jolt_list');
    }

    // --- Dynamic inputs ---

    insertDynamicInput(actionObject, placeholder, isDisabled) {
        if (isDisabled) return;
        const field = this.getFieldForLastFocus();
        if (field && actionObject[field] !== undefined) {
            const currentValue = actionObject[field] || '';
            const separator = currentValue.length > 0 && !currentValue.endsWith(' ') && !currentValue.endsWith('\n') ? ' ' : '';
            actionObject[field] = `${currentValue}${separator}$${placeholder}`;
            // Refresh disabled states after inserting
            this.updateDynamicInputs();
        }
    }

    getFieldForLastFocus() {
        const focusMap = {
            subject: 'subject', body: 'body', introduction: 'introduction', message: 'message',
            workOrderDescription: 'workOrderDescription', assetId: 'assetId', taskId: 'taskId',
            customerId: 'customerId', clientId: 'clientId', clientSecret: 'clientSecret',
            companyName: 'companyName', subTypeId: 'subTypeId', contactName: 'contactName',
            contactAddress: 'contactAddress', storeId: 'storeId', assetTagId: 'assetTagId',
            description: 'description'
        };
        return focusMap[this.lastActionFocus];
    }

    updateDynamicInputs() {
        // All placeholder definitions: [key, description]
        const defs = {
            name: ['name', 'Device name (device ID if no name is set)'],
            description: ['description', 'Device description'],
            deviceID: ['deviceID', 'Device ID'],
            label: ['label.labelKey', 'Access label values by providing the labelKey'],
            triggerTime: ['triggerTime', 'Timestamp in the project\'s timezone when the device met the trigger condition.'],
            timeSinceTrigger: ['timeSinceTrigger', 'Time since the device met the trigger condition.'],
            projectDisplayName: ['projectDisplayName', 'Project display name'],
            projectID: ['projectID', 'Project ID'],
            triggerStatus: ['triggerStatus', 'Status for the trigger: Triggering or Resolved'],
            celsius: ['celsius', 'Temperature sensor value in °C'],
            fahrenheit: ['fahrenheit', 'Temperature sensor value in °F'],
            co2: ['co2', 'CO2 sensor value in PPM'],
            pascal: ['pascal', 'Differential pressure sensor value in Pa'],
            inH2O: ['inH2O', 'Differential pressure sensor value in inH2O'],
            motion: ['motion', 'Motion sensor status as "motion detected" or "no motion detected"'],
            deskOccupancy: ['deskOccupancy', 'Desk occupancy state as "occupied" or "not occupied"'],
            openOrClosed: ['openOrClosed', 'Door/Window status as "open" or "closed"'],
            proximityStatus: ['proximityStatus', 'Proximity sensor status as "open" or "closed"'],
            objectPresent: ['objectPresent', 'Proximity sensor status as "present" or "not present"'],
            relativeHumidity: ['relativeHumidity', 'Relative humidity value as %RH'],
            waterPresent: ['waterPresent', 'Water sensor status as "water present" or "no water present"'],
            batteryState: ['batteryState', 'Battery state as "ok", "low" or "critical"'],
        };

        const universalKeys = ['name', 'description', 'deviceID', 'label', 'timeSinceTrigger', 'triggerTime', 'projectDisplayName', 'projectID'];

        const triggerSpecificKeys = {
            temperature: ['triggerStatus', 'celsius', 'fahrenheit'],
            virtualGlycolBuffer: ['triggerStatus', 'celsius', 'fahrenheit'],
            relativeHumidity: ['triggerStatus', 'relativeHumidity', 'celsius', 'fahrenheit'],
            co2: ['triggerStatus', 'co2'],
            differentialPressure: ['triggerStatus', 'pascal', 'inH2O'],
            motion: ['triggerStatus', 'motion'],
            deskOccupancy: ['triggerStatus', 'deskOccupancy'],
            contact: ['triggerStatus', 'openOrClosed'],
            objectPresent: ['triggerStatus', 'proximityStatus', 'objectPresent'],
            waterPresent: ['triggerStatus', 'waterPresent'],
            connectionStatus: ['triggerStatus'],
            batteryStatus: ['triggerStatus', 'batteryState'],
            touch: [],
            touchCount: [],
            proximityCount: [],
            contactCount: [],
        };

        // Ordered list of all placeholder keys for display
        const allSpecificKeys = new Set();
        Object.values(triggerSpecificKeys).forEach(keys => keys.forEach(k => allSpecificKeys.add(k)));
        const orderedKeys = ['triggerStatus', ...universalKeys, ...allSpecificKeys].filter((k, i, arr) => arr.indexOf(k) === i);

        if (this.triggerField) {
            // Alert template context: show only matching placeholders (no disabled state)
            const keys = new Set(universalKeys);
            const specific = triggerSpecificKeys[this.triggerField];
            if (specific) specific.forEach(k => keys.add(k));

            // On a virtualGlycolBuffer trigger, $celsius/$fahrenheit resolve to the glycol temperature
            const triggerDefs = this.triggerField === 'virtualGlycolBuffer'
                ? { ...defs, celsius: ['celsius', 'Virtual glycol buffer temperature in °C'], fahrenheit: ['fahrenheit', 'Virtual glycol buffer temperature in °F'] }
                : defs;

            this.availableDynamicInputs = orderedKeys
                .filter(k => keys.has(k))
                .map(k => triggerDefs[k])
                .filter(Boolean);
            return;
        }

        // Standalone escalation template: show ALL, disable incompatible ones
        const usedPlaceholders = this.getUsedPlaceholders();
        const compatibleTriggers = this.getCompatibleTriggers(usedPlaceholders, triggerSpecificKeys);

        // Build the allowed set based on compatible triggers
        const allowedKeys = new Set(universalKeys);
        allowedKeys.add('triggerStatus'); // always allowed
        if (compatibleTriggers === null) {
            // No trigger-specific placeholders used yet — everything allowed
            allSpecificKeys.forEach(k => allowedKeys.add(k));
        } else {
            compatibleTriggers.forEach(triggerType => {
                const specific = triggerSpecificKeys[triggerType];
                if (specific) specific.forEach(k => allowedKeys.add(k));
            });
        }

        this.availableDynamicInputs = orderedKeys
            .filter(k => defs[k])
            .map(k => {
                const entry = [...defs[k]];
                entry.push(!allowedKeys.has(k)); // third element: disabled flag
                return entry;
            });

        this.$scope.$applyAsync();
    }

    getUsedPlaceholders() {
        const used = new Set();
        if (!this.escalationLevels) return used;

        this.escalationLevels.forEach(level => {
            if (!level.actions) return;
            level.actions.forEach(action => {
                const texts = [];
                if (action.sms) texts.push(action.sms.body);
                if (action.email) { texts.push(action.email.subject); texts.push(action.email.body); }
                if (action.phoneCall) { texts.push(action.phoneCall.introduction); texts.push(action.phoneCall.message); }

                texts.forEach(text => {
                    if (!text) return;
                    const matches = text.match(/\$([a-zA-Z]+)/g) || [];
                    matches.forEach(m => used.add(m.substring(1)));
                });
            });
        });

        return used;
    }

    getCompatibleTriggers(usedPlaceholders, triggerSpecificKeys) {
        // Find which trigger-specific placeholders are used (exclude universal + triggerStatus)
        const universalSet = new Set(['name', 'description', 'deviceID', 'label', 'timeSinceTrigger', 'triggerTime', 'projectDisplayName', 'projectID', 'triggerStatus']);
        const usedSpecific = [...usedPlaceholders].filter(p => !universalSet.has(p));

        if (usedSpecific.length === 0) return null; // No constraint — all triggers compatible

        // Find which trigger types contain ALL used specific placeholders
        const compatible = [];
        Object.entries(triggerSpecificKeys).forEach(([triggerType, keys]) => {
            if (usedSpecific.every(p => keys.includes(p))) {
                compatible.push(triggerType);
            }
        });

        return compatible;
    }

    // --- Contact group methods ---

    getContactGroupDisplayName(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.displayName : resourceName;
    }

    getContactGroupDescription(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.description : '';
    }

    addContactGroupToAction(action, contactGroupName) {
        let actionObject;
        if (action.type === 'PHONE_CALL') {
            actionObject = action.phoneCall;
        } else {
            actionObject = action[action.type.toLowerCase()];
        }
        if (!actionObject.contactGroups) {
            actionObject.contactGroups = [];
        }
        if (!actionObject.contactGroups.includes(contactGroupName)) {
            actionObject.contactGroups.push(contactGroupName);
            this.recomputeAllWarnings();
        }
    }

    removeContactGroupFromAction(action, contactGroupName) {
        const actionObject = action[action.type.toLowerCase()];
        if (actionObject.contactGroups) {
            const index = actionObject.contactGroups.indexOf(contactGroupName);
            if (index > -1) {
                actionObject.contactGroups.splice(index, 1);
                this.recomputeAllWarnings();
            }
        }
    }

    // --- Warning computation ---

    recomputeAllWarnings() {
        this.computedWarnings = {};
        if (!this.escalationLevels) return;

        const actionKeys = { SMS: 'sms', PHONE_CALL: 'phoneCall', EMAIL: 'email' };

        this.escalationLevels.forEach(level => {
            if (!level.actions) return;
            level.actions.forEach(action => {
                Object.keys(actionKeys).forEach(type => {
                    const actionObj = action[actionKeys[type]];
                    if (!actionObj || !actionObj.contactGroups) return;
                    actionObj.contactGroups.forEach(cgName => {
                        if (!this.computedWarnings[cgName]) {
                            this.computedWarnings[cgName] = {};
                        }
                        if (!this.computedWarnings[cgName][type]) {
                            this.computedWarnings[cgName][type] = this.computeWarningsFor(cgName, type);
                        }
                    });
                });
            });
        });
    }

    computeWarningsFor(contactGroupResourceName, actionType) {
        if (!this.contactGroupInfo || !(this.contactGroupInfo instanceof Map)) return [];

        if (!this.contactGroupInfo.has(contactGroupResourceName)) {
            this.fetchContactGroupInfoIfNeeded(contactGroupResourceName);
            return [];
        }

        const info = this.contactGroupInfo.get(contactGroupResourceName);
        if (!info) return [];

        const warnings = [];
        const scopeProjects = this.getProjectsInScope();
        const totalProjects = scopeProjects.length;

        if (info.totalContacts === 0) {
            warnings.push({
                message: 'This contact group has no contacts',
                missingProjects: scopeProjects.map(p => ({
                    name: p.name, displayName: p.displayName || p.name,
                    inlineName: '', inlineEmail: '', inlinePhone: ''
                })),
                totalProjects
            });
            return warnings;
        }

        if (info.byProject && totalProjects > 0) {
            const projectsMissingContacts = [];
            const projectsMissingType = [];

            scopeProjects.forEach(project => {
                const projectInfo = info.byProject.get(project.name);
                const entry = {
                    name: project.name, displayName: project.displayName || project.name,
                    inlineName: '', inlineEmail: '', inlinePhone: ''
                };
                if (!projectInfo) {
                    projectsMissingContacts.push(entry);
                } else if ((actionType === 'SMS' || actionType === 'PHONE_CALL') && !projectInfo.hasPhones) {
                    projectsMissingType.push(entry);
                } else if (actionType === 'EMAIL' && !projectInfo.hasEmails) {
                    projectsMissingType.push(entry);
                }
            });

            if (projectsMissingContacts.length > 0) {
                warnings.push({
                    message: `No contacts in ${projectsMissingContacts.length} of ${totalProjects} projects`,
                    missingProjects: projectsMissingContacts, totalProjects
                });
            }

            const typeLabel = actionType === 'EMAIL' ? 'email addresses' : 'phone numbers';
            if (projectsMissingType.length > 0) {
                warnings.push({
                    message: `No ${typeLabel} in ${projectsMissingType.length} of ${totalProjects} projects`,
                    missingProjects: projectsMissingType, totalProjects
                });
            }
        } else {
            if ((actionType === 'SMS' || actionType === 'PHONE_CALL') && !info.hasPhones) {
                warnings.push({ message: 'No contacts in this group have phone numbers', missingProjects: [], totalProjects: 0 });
            }
            if (actionType === 'EMAIL' && !info.hasEmails) {
                warnings.push({ message: 'No contacts in this group have email addresses', missingProjects: [], totalProjects: 0 });
            }
        }

        return warnings;
    }

    getContactGroupWarnings(contactGroupResourceName, actionType) {
        if (!this.computedWarnings) return [];
        const groupWarnings = this.computedWarnings[contactGroupResourceName];
        if (!groupWarnings) return [];
        return groupWarnings[actionType] || [];
    }

    getProjectsInScope() {
        if (this.matchingProjects && this.matchingProjects.length > 0) {
            return this.matchingProjects;
        }
        return this.allProjects || [];
    }

    toggleWarningExpand(contactGroupResourceName, warningIndex) {
        const key = `${contactGroupResourceName}_${warningIndex}`;
        if (this.expandedWarnings.has(key)) {
            this.expandedWarnings.delete(key);
        } else {
            this.expandedWarnings.add(key);
        }
    }

    isWarningExpanded(contactGroupResourceName, warningIndex) {
        return this.expandedWarnings.has(`${contactGroupResourceName}_${warningIndex}`);
    }

    // --- Contact saving ---

    async saveAllContacts(contactGroupResourceName, missingProjects, actionType) {
        const projectsToSave = missingProjects.filter(project => {
            if (project.saved || project.saving) return false;
            const hasName = project.inlineName && project.inlineName.trim();
            const hasEmail = project.inlineEmail && project.inlineEmail.trim();
            const hasPhone = project.inlinePhone && project.inlinePhone.trim();
            if (!hasName) return false;
            if (actionType === 'EMAIL') return hasEmail;
            return hasPhone;
        });

        if (projectsToSave.length === 0) {
            this.ToastService.showSimpleText(`No rows to save. Fill in Name and ${actionType === 'EMAIL' ? 'Email' : 'Phone Number'} to add contacts.`);
            return;
        }

        if (actionType === 'SMS' || actionType === 'PHONE_CALL') {
            const invalidPhone = projectsToSave.find(p => !p.inlinePhone.trim().startsWith('+'));
            if (invalidPhone) {
                this.ToastService.showSimpleText(`Phone number for "${invalidPhone.displayName}" must start with "+" followed by country code`);
                return;
            }
        }

        const warnings = this.getContactGroupWarnings(contactGroupResourceName, actionType);
        const warning = warnings.find(w => w.missingProjects === missingProjects);

        if (warning) warning.savingAll = true;
        projectsToSave.forEach(p => { p.saving = true; });
        this.$scope.$applyAsync();

        const results = await this.saveContactsBatched(projectsToSave, contactGroupResourceName, actionType);
        const { savedCount, failedCount } = results;

        if (warning) warning.savingAll = false;

        if (failedCount === 0) {
            this.ToastService.showSimpleText(`${savedCount} contact${savedCount > 1 ? 's' : ''} added successfully`);
        } else {
            this.ToastService.showSimpleText(`${savedCount} added, ${failedCount} failed`);
        }

        this.pendingContactGroupFetches.delete(contactGroupResourceName);
        await this.fetchContactGroupInfoIfNeeded(contactGroupResourceName);
        this.$scope.$applyAsync();
    }

    saveContact(project, contactGroupResourceName, actionType) {
        const projectId = project.name.split('/')[1];
        const displayName = project.inlineName.trim();
        const email = actionType === 'EMAIL' ? project.inlineEmail.trim() : null;
        const phone = (actionType === 'SMS' || actionType === 'PHONE_CALL') ? project.inlinePhone.trim() : null;

        return this.IAMService.createContact(projectId, {
            displayName,
            email: email || null,
            phoneNumber: phone || null,
            contactGroup: contactGroupResourceName
        }).then(() => {
            project.saved = true;
        }).catch((error) => {
            console.error(`Failed to add contact to ${project.displayName}:`, error); // eslint-disable-line no-console
            project.saving = false;
            project.error = error.data?.error || error.message || 'Failed';
        });
    }

    async saveContactsBatched(projectsToSave, contactGroupResourceName, actionType) {
        const batchSize = 5;
        for (let i = 0; i < projectsToSave.length; i += batchSize) {
            const batch = projectsToSave.slice(i, i + batchSize);
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(batch.map(p => this.saveContact(p, contactGroupResourceName, actionType)));
            this.$scope.$applyAsync();
        }
        const savedCount = projectsToSave.filter(p => p.saved).length;
        const failedCount = projectsToSave.filter(p => p.error).length;
        return { savedCount, failedCount };
    }

    async fetchContactGroupInfoIfNeeded(contactGroupResourceName) {
        if (this.pendingContactGroupFetches.has(contactGroupResourceName)) return;
        this.pendingContactGroupFetches.add(contactGroupResourceName);

        try {
            const organizationName = this.ProjectManager.currentProject.organization;
            const response = await this.IAMService.listContactsForOrganization({
                organization: organizationName,
                contactGroup: contactGroupResourceName
            });
            const contacts = response.contacts || [];
            this.contactGroupInfo = new Map(this.contactGroupInfo);
            this.contactGroupInfo.set(contactGroupResourceName, buildContactGroupInfo(contacts));
            this.recomputeAllWarnings();
            this.$scope.$applyAsync();
        } catch (error) {
            console.error(`Failed to fetch contact info for ${contactGroupResourceName}:`, error); // eslint-disable-line no-console
        } finally {
            this.pendingContactGroupFetches.delete(contactGroupResourceName);
        }
    }

    // --- Notifications ---

    notifyLevelsChange() {
        if (this.onLevelsChange) {
            this.onLevelsChange({ $event: this.escalationLevels });
        }
    }
}
